import { useEffect, useState, type FormEvent } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import * as api from '../../../../api';

import { Button, Margin } from '../../../../components/Button';
import { ModalContainer } from '../../../../components/ModalContainer';

import defaultStyles from '../../../../styles/formSetStyles.module.css';
import styles from './index.module.css';
import Enum from '../../../../components/Question/Enum';
import { DatePicker } from '../../../../components/DatePicker';
import { getOptionsFromArray } from '../../../../components/utils';

interface Props {
  isModalOpen: boolean;
  setIsModalOpen: (value: boolean) => void;
  bookSlot: (timeslot: api.EhrTimeSlot.EhrTimeSlot) => Promise<void>;
  selectedTimeSlot: api.EhrTimeSlot.EhrTimeSlot | null;
  characterLimit?: number;
  collectSchedulingPreferences?: boolean;
}

const CommentModal: React.FC<Props> = ({
  isModalOpen,
  setIsModalOpen,
  bookSlot,
  selectedTimeSlot,
  characterLimit = 100,
  collectSchedulingPreferences,
}) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [charactersLeft, setCharactersLeft] = useState<number>(characterLimit);
  // Mammogram specific logic added to support mammogram direct scheduling.
  // Ticket for tracking adding configurability to experience: https://app.asana.com/0/1201935264203449/1203044238364071
  const [mammogramPerformed, setMammogramPerformed] = useState<string | null>(
    null,
  );
  const [mammogramPerformedDate, setMammogramPerformedDate] =
    useState<Date | null>(null);

  useEffect(() => {
    if (selectedTimeSlot?.appointmentKind.toLowerCase().includes('mammo')) {
      setMammogramPerformed('Yes');
    } else {
      setMammogramPerformed(null);
    }
  }, [selectedTimeSlot?.appointmentKind]);

  const returnHardcodedMammoModal = () => {
    return (
      <>
        <div className={classNames(defaultStyles.SubLabel, styles.SubLabel)}>
          {t('Was your last mammogram performed within our provider network?')}
        </div>
        <Enum
          listClassName={styles.Enum}
          onChange={(option) => setMammogramPerformed(option)}
          options={mammogramOptions}
          value={mammogramPerformed ?? 'Yes'}
          useAlternateEnum={false}
        />
        {mammogramPerformed === 'No' ? (
          <>
            <div
              className={classNames(defaultStyles.SubLabel, styles.SubLabel)}
            >
              {t('Where was your last mammogram performed? (optional)')}
            </div>
            <input
              className={styles.Input}
              value={comment || ''}
              placeholder={t('Add the name of the location')}
              onChange={(e) => {
                setComment(e.currentTarget.value);
                setCharactersLeft(
                  characterLimit - e.currentTarget.value.length,
                );
              }}
              maxLength={characterLimit}
            />
            <div
              className={classNames(defaultStyles.SubLabel, styles.SubLabel)}
            >
              {t('When was your last mammogram performed? (optional)')}
            </div>
            <DatePicker
              className={classNames(defaultStyles.SubLabel, styles.SubLabel)}
              curvedInput={5}
              date={undefined}
              onChange={(date) => setMammogramPerformedDate(date)}
            />
          </>
        ) : null}
      </>
    );
  };

  const handleBookingSlot = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    if (!selectedTimeSlot) {
      alert(t('Something went wrong, please select another time slot.'));
      setIsModalOpen(false);
      return;
    }

    await bookSlot({
      ...selectedTimeSlot,
      ...getComment(),
    });
    setIsSubmitting(false);
    setIsModalOpen(false);
  };

  const returnModalInput = () => {
    if (mammogramPerformed) {
      return returnHardcodedMammoModal();
    }
    return (
      <>
        <div className={classNames(defaultStyles.SubLabel, styles.SubLabel)}>
          {t('Please briefly describe the reason for your visit')}
          {collectSchedulingPreferences
            ? ' and provide any schedule preferences for follow-up appointments'
            : ''}
        </div>
        {collectSchedulingPreferences ? (
          <textarea
            className={`${styles.Input} ${styles.TextArea}`}
            value={comment || ''}
            placeholder={
              'ex. knee pain, Tues/Thurs afternoons preferred; unavailable on Fri'
            }
            onChange={(e) => {
              setComment(e.currentTarget.value);
              setCharactersLeft(characterLimit - e.currentTarget.value.length);
            }}
            maxLength={characterLimit}
          />
        ) : (
          <input
            className={styles.Input}
            value={comment || ''}
            placeholder={t('Example: rash on right leg')}
            onChange={(e) => {
              setComment(e.currentTarget.value);
              setCharactersLeft(characterLimit - e.currentTarget.value.length);
            }}
            maxLength={characterLimit}
          />
        )}
      </>
    );
  };

  const getComment = () => {
    if (mammogramPerformed === 'No') {
      return {
        comment: `Last mammogram performed at ${
          comment ? comment : 'unspecified'
        } on ${
          mammogramPerformedDate
            ? mammogramPerformedDate.toDateString()
            : 'unspecified'
        }.`,
      };
    }
    if (mammogramPerformed === 'Yes') {
      return {
        comment: `Last mammogram performed within the same provider network.`,
      };
    }
    return comment ? { comment: comment } : {};
  };

  const disableCommentButton = () => {
    if (mammogramPerformed === 'No') {
      return false;
    }
    if (mammogramPerformed === 'Yes') {
      return false;
    }
    return isSubmitting || !comment?.length;
  };

  const mammogramOptions = getOptionsFromArray(['Yes', 'No'], t);
  return (
    <ModalContainer
      contentClassName={styles.ModalContainer}
      isOpen={isModalOpen}
      onModalClose={() => setIsModalOpen(false)}
      topLabel={t('Reason for Visit')}
    >
      <form
        className={classNames(styles.Inputs, styles.RegistrationInputs)}
        onSubmit={handleBookingSlot}
      >
        {returnModalInput()}
        <div
          className={classNames(styles.HelpText)}
          hidden={charactersLeft > 0}
        >
          {characterLimit === 100 ? (
            <p>{t('Only 100 characters allowed')}</p>
          ) : (
            <p>{`Only ${characterLimit} characters allowed`}</p>
          )}
        </div>
        <Button
          className={styles.Button}
          type="submit"
          children={isSubmitting ? t('Loading...') : t('Continue')}
          disabled={disableCommentButton()}
          margin={Margin.Modal}
        />
      </form>
    </ModalContainer>
  );
};

export default CommentModal;
